<template>

  <b-card title="Invoice Timeline">

    <div v-if="loading" class="text-muted small">

      Loading timeline…

    </div>

    <template v-else>

      <b-alert

        v-if="showRevisionGap"

        variant="warning"

        show

        class="small py-1 mb-2"

      >

        <template v-if="isMissingHistoryRoute">

          Status is <strong>Revised</strong>, but

          <code>GET /invoice/:id/revision/history</code>

          is missing (404). No fake timeline rows are shown. Backend must INSERT into

          <code>invoice_revisions</code> on Revised PUT and expose that history route.

        </template>

        <template v-else>

          Status is <strong>Revised</strong>, but

          <code>invoice_revisions</code> returned no rows for this invoice.

          The client only updates via FormData PUT — it cannot insert the log row.

          A “Revised” timeline event appears only after the backend INSERTs on

          <code>PUT /invoice/update/:id</code> when <code>status=Revised</code>

          (or <code>is_revision=1</code>) and history GET returns those rows.

        </template>

      </b-alert>

      <div v-if="!displayEvents.length" class="text-muted small">

        No timeline activity yet. Events appear when the invoice is created and when revisions are saved.

      </div>

      <template v-else>

        <app-timeline v-for="event in displayEvents" :key="event.id">

          <app-timeline-item

            :variant="event.type"

            :time="formatEventTime(event)"

          >

            <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0">

              <h6>{{ event.title }}</h6>

            </div>

            <p class="mb-0">{{ event.description }}</p>

            <div v-if="event.documentLinks && event.documentLinks.length" class="mt-50 d-flex flex-wrap" style="gap: 0.35rem;">

              <b-button

                v-for="(link, idx) in event.documentLinks"

                :key="idx"

                size="sm"

                variant="outline-primary"

                class="py-0 px-1"

                @click="openDocument(link.path)"

              >

                <feather-icon icon="FileTextIcon" size="12" class="mr-25" />

                {{ documentLinkLabel(link) }}

              </b-button>

            </div>

          </app-timeline-item>

        </app-timeline>

        <small v-if="displayEvents.length >= 50" class="text-muted">

          Please note only top 50 activities are displayed here, for more activities please contact IT

        </small>

      </template>

    </template>

  </b-card>

</template>



<script>

import { BCard, BButton, BAlert } from 'bootstrap-vue'

import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'

import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'

import { loadInvoiceFileDataUrl } from '@/utils/invoice-file-loader'

import { isRevisedStatus, timelineHasRevisedEvent } from '@/utils/invoice-timeline-builder'



export default {

  components: {

    BCard,

    BButton,

    BAlert,

    AppTimeline,

    AppTimelineItem,

  },

  props: {

    invoiceTimeline: {

      type: Array,

      default: () => [],

    },

    loading: {

      type: Boolean,

      default: false,

    },

    /** Live invoice status — used for honest gap messaging. */

    invoiceStatus: {

      type: String,

      default: '',

    },

    /**

     * Optional meta from invoiceTimelineService (_meta).

     * revisionHistoryGap: status Revised but no revision rows.

     */

    timelineMeta: {

      type: Object,

      default: null,

    },

  },

  computed: {

    displayEvents() {

      return Array.isArray(this.invoiceTimeline) ? this.invoiceTimeline : []

    },

    showRevisionGap() {

      if (this.timelineMeta && this.timelineMeta.revisionHistoryGap === true) return true

      if (!isRevisedStatus(this.invoiceStatus)) return false

      return !timelineHasRevisedEvent(this.displayEvents)

    },

    isMissingHistoryRoute() {

      return !!(this.timelineMeta && this.timelineMeta.revisionSource === 'missing-route')

    },

  },

  methods: {

    formatEventTime(event) {

      const raw = event.createdAt

      if (!raw || raw === '—') return `—  by  ${event.createdBy || '—'}`

      const formatted = String(raw).replace(/T/, ' ').replace(/\..+/, '')

      return `${formatted}  by  ${event.createdBy || '—'}`

    },

    documentLinkLabel(link) {

      if (!link) return 'Document'

      if (link.kind === 'revised') return 'Revised PDF'

      if (link.kind === 'r1') return 'R1 doc'

      if (link.kind === 'note') return 'Note'

      if (link.kind === 'original') return 'Original PDF'

      return link.label || 'Document'

    },

    async openDocument(path) {

      const url = await loadInvoiceFileDataUrl(path)

      if (url) {

        window.open(url, '_blank', 'noopener,noreferrer')

      }

    },

  },

}

</script>



<style>

.timeline-item {

  padding-bottom: 15px;

}

</style>

